import { Component, inject, OnInit, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { BrandService } from '../../../services/brand.service';
import { CategoryService } from '../../../services/category.service';
import { ModelService } from '../../../services/model.service';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { FormErrorComponent } from '../../../../shared/components/form-error/form-error.component';
import { FormServerErrorComponent } from '../../../../shared/components/form-server-error/form-server-error.component';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { InputTextModule } from 'primeng/inputtext';
import { ApiResult } from '../../../../shared/models/api-result.model';
import { CreateModel, Model, UpdateModel } from '../../../models/model.model';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { FormGroupService } from '../../../../shared/services/form-group.service';
import { DropdownOption } from '../../../../shared/models/dropdowm.model';
import { ToolbarModule } from 'primeng/toolbar';
import { ToastModule } from 'primeng/toast';

@Component({
  selector: 'app-model-create-or-update',
  imports: [
    ReactiveFormsModule,
    BreadcrumbModule,
    FormErrorComponent,
    FormServerErrorComponent,
    SelectModule,
    ToggleSwitchModule,
    ButtonModule,
    ProgressSpinnerModule,
    InputTextModule,
    RouterLink,
    ToolbarModule,
    ToastModule,
  ],
  templateUrl: './model-create-or-update.component.html',
})
export class ModelCreateOrUpdateComponent implements OnInit {
  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);
  private formGroupService = inject(FormGroupService);
  private router = inject(Router);
  private modelService = inject(ModelService);
  private brandService = inject(BrandService);
  private categoryService = inject(CategoryService);
  private activatedRoute = inject(ActivatedRoute);

  private readonly maxImageSizeBytes = 5 * 1024 * 1024; // 5MB

  formGroup: FormGroup = this.fb.group({
    id: [null],
    name: [null, [Validators.required, Validators.maxLength(255)]],
    description: [null, [Validators.maxLength(255)]],
    brandId: [null, Validators.required],
    categoryId: [null, Validators.required],
    image: [null],
    imageFileId: [null],
    imageUrl: [null],
    isActive: [true],
  });

  loading = signal(false);

  modelId = signal<string | null>(null);

  brandSelectLoading = signal(false);
  categorySelectLoading = signal(false);

  brandsDropdown = signal<DropdownOption[]>([]);
  categoriesDropdown = signal<DropdownOption[]>([]);

  selectedImagePreviewUrl = signal<string | null>(null);
  private selectedImageObjectUrl: string | null = null;

  ngOnInit(): void {
    this.loadBrandsDropdown();
    this.loadCategoryDropdown();

    this.activatedRoute.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.modelId.set(id);
        this.loadData(id);
      }
    });
  }

  ngOnDestroy(): void {
    this.revokeSelectedLogoObjectUrl();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];

    if (!file.type!.startsWith('image/')) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: `O arquivo deve ser uma imagem (ex: jpg, png, webp, etc.).`,
        life: 5000,
      });
      return;
    }

    if (file.size > this.maxImageSizeBytes) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: `A image deve ter no máximo 5MB.`,
        life: 5000,
      });
      return;
    }

    if (
      this.formGroup.get('imageUrl')?.value ||
      this.formGroup.get('imageFileId')?.value
    ) {
      this.formGroup.patchValue({
        imageUrl: null,
        imageFileId: null,
      });
    }

    this.revokeSelectedLogoObjectUrl();
    this.selectedImageObjectUrl = URL.createObjectURL(file);
    this.selectedImagePreviewUrl.set(this.selectedImageObjectUrl);

    this.formGroup.patchValue({ image: file });
    this.formGroup.get('image')!.updateValueAndValidity();
  }

  removeLogo(): void {
    this.formGroup.patchValue({
      image: null,
      imageFileId: null,
      imageUrl: null,
    });

    this.formGroup.get('image')?.updateValueAndValidity();

    this.revokeSelectedLogoObjectUrl();
    this.selectedImagePreviewUrl.set(null);
  }

  onSubmit() {
    if (!this.formGroup.valid) {
      Object.keys(this.formGroup.controls).forEach((key) => {
        const control = this.formGroup.get(key);
        control?.markAsTouched();
      });
      return;
    }
    let data = this.formGroup.getRawValue();
    if (data.id) {
      data.brandId = data.brandId?.value;
      data.categoryId = data.categoryId?.value;
      this.update(data as UpdateModel);
    } else {
      let data = this.formGroup.getRawValue();
      delete data.id;
      data.brandId = data.brandId?.value;
      data.categoryId = data.categoryId?.value;
      this.create(data as CreateModel);
    }
  }

  private loadBrandsDropdown() {
    this.brandSelectLoading.set(true);
    this.brandService.getAllDropdown().subscribe({
      next: (response: ApiResult) => {
        const options = response.data as DropdownOption[];
        this.brandsDropdown.set(options);
        this.brandSelectLoading.set(false);
      },
      error: (e) => {
        this.brandSelectLoading.set(false);

        if (e instanceof HttpErrorResponse) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao carregar as marcas',
            life: 3000,
          });
        }
      },
    });
  }

  private loadCategoryDropdown() {
    this.categorySelectLoading.set(true);
    this.categoryService.getAllDropdown().subscribe({
      next: (response: ApiResult) => {
        const options = response.data as DropdownOption[];
        this.categoriesDropdown.set(options);
        this.categorySelectLoading.set(false);
      },
      error: (e) => {
        this.categorySelectLoading.set(false);

        if (e instanceof HttpErrorResponse) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao carregar as categorias ',
            life: 3000,
          });
        }
      },
    });
  }

  private loadData(id: string): void {
    this.loading.set(true);

    this.modelService.getById(id).subscribe({
      next: (response: ApiResult) => {
        this.loading.set(false);

        const model = response.data as Model;
        this.formGroup.patchValue(model);

        this.formGroup.get('brandId')?.setValue({
          label: model.brandName,
          value: model.brandId,
        });

        this.formGroup.get('categoryId')?.setValue({
          label: model.categoryName,
          value: model.categoryId,
        });
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao carregar a marca',
            life: 3000,
          });
        }
      },
    });
  }

  private create(data: CreateModel): void {
    this.loading.set(true);

    const formValue = this.formGroup.value;

    const request = new FormData();
    request.append('name', data.name);
    request.append('description', data.description ?? '');
    request.append('brandId', data.brandId);
    request.append('categoryId', data.categoryId);
    if (formValue.image) {
      request.append('image', formValue.image);
    }

    this.modelService.create(request).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso!',
          detail: 'Modelo cadastrado com sucesso!',
          life: 2000,
        });

        this.loading.set(false);
        this.router.navigate(['/models']);
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          if ([400, 422].includes(e.status)) {
            if (
              (e.error.message &&
                (e.error.errors || e.error.errors.length > 0)) ||
              (!e.error.message &&
                (e.error.errors || e.error.errors.length > 0))
            ) {
              this.formGroupService.addServerErrors(
                e.error.errors,
                this.formGroup,
              );
            } else if (
              e.error.message &&
              (!e.error.errors || e.error.errors.length == 0)
            ) {
              this.messageService.add({
                severity: 'warn',
                summary: 'Warning',
                detail: e.error.message,
                life: 3000,
              });
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Fail',
              detail: 'Ocorreu um erro ao cadastrar o modelo',
              life: 3000,
            });
          }
        }
      },
    });
  }

  private update(data: UpdateModel): void {
    this.loading.set(true);

    const formValue = this.formGroup.value;

    const request = new FormData();
    request.append('id', data.id);
    request.append('name', data.name);
    request.append('description', data.description ?? '');
    request.append('brandId', data.brandId);
    request.append('categoryId', data.categoryId);
    request.append('isActive', data.isActive.toString());

    if (data.imageFileId) request.append('imageFileId', data.imageFileId);
    if (formValue.image) request.append('image', formValue.image);

    this.modelService.update(data.id, request).subscribe({
      next: () => {
        this.loading.set(false);

        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso!',
          detail: 'Modelo atualizado com sucesso!',
          life: 2000,
        });

        this.router.navigate(['/models']);
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          if ([400, 422].includes(e.status)) {
            if (
              (e.error.message &&
                (e.error.errors || e.error.errors.length > 0)) ||
              (!e.error.message &&
                (e.error.errors || e.error.errors.length > 0))
            ) {
              this.formGroupService.addServerErrors(
                e.error.errors,
                this.formGroup,
              );
            } else if (
              e.error.message &&
              (!e.error.errors || e.error.errors.length == 0)
            ) {
              this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: e.error.message,
                life: 3000,
              });
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao atualizar o modelo',
              life: 3000,
            });
          }
        }
      },
    });
  }

  private revokeSelectedLogoObjectUrl(): void {
    if (this.selectedImageObjectUrl) {
      URL.revokeObjectURL(this.selectedImageObjectUrl);
      this.selectedImageObjectUrl = null;
    }
  }
}
